import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from 'src/@/components/ui/dialog';
import Iconify from 'src/components/Iconify';
import { resolvePlatform } from 'src/design/tokens';
import { STATUS_MAP } from './constants';
import MktPostComments, { commentTargets } from './MktPostComments';

function formatDate(dateStr, locale) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

function buildPreviewAccounts(post, accounts) {
    const byId = new Map((accounts || []).map((a) => [a._id, a]));
    const map = {};
    (post.account_ids || []).forEach((ref) => {
        const id = typeof ref === 'string' ? ref : ref?._id;
        const acc = byId.get(id);
        if (acc) map[acc.provider] = acc;
    });
    (post.platforms || []).forEach((p) => {
        if (!map[p]) {
            const acc = (accounts || []).find((a) => a.provider === p);
            if (acc) map[p] = acc;
        }
    });
    return map;
}

function MediaStage({ media, postType, t }) {
    const [index, setIndex] = useState(0);

    useEffect(() => { setIndex(0); }, [media]);

    const isVertical = postType === 'story' || postType === 'reel';
    const items = media || [];
    const item = items[index];
    const hasMultiple = items.length > 1;

    if (!item) {
        return (
            <div className="relative w-full h-full min-h-[360px] md:min-h-[560px] flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-slate-400 gap-3">
                <Iconify icon="eva:file-text-outline" width={48} />
                <span className="text-sm">{t('posts.preview.noMedia')}</span>
            </div>
        );
    }

    const go = (dir) => (e) => {
        e.stopPropagation();
        setIndex((prev) => (prev + dir + items.length) % items.length);
    };

    return (
        <div className={`relative w-full ${isVertical ? 'aspect-[9/16] max-h-[78vh]' : 'aspect-square'} md:h-full md:aspect-auto md:min-h-[560px] bg-black flex items-center justify-center overflow-hidden`}>
            {item.type === 'video' ? (
                item.url ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video src={item.url} poster={item.thumbnail} controls className="max-w-full max-h-full object-contain" />
                ) : (
                    <img src={item.thumbnail} alt="" className="max-w-full max-h-full object-contain" />
                )
            ) : (
                <img src={item.url} alt="" className="max-w-full max-h-full object-contain" />
            )}

            {hasMultiple && (
                <>
                    <button
                        type="button"
                        onClick={go(-1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                    >
                        <Iconify icon="eva:arrow-ios-back-fill" width={22} />
                    </button>
                    <button
                        type="button"
                        onClick={go(1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                    >
                        <Iconify icon="eva:arrow-ios-forward-fill" width={22} />
                    </button>
                    <span className="absolute top-3 right-3 bg-black/60 text-white text-[11px] font-medium px-2 py-0.5 rounded-full">
                        {index + 1}/{items.length}
                    </span>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                        {items.map((_, i) => (
                            <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === index ? 'bg-white' : 'bg-white/40'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default function MktPostPreviewModal({ open, post, accounts, onClose, onEdit, onPublish, onDelete }) {
    const { t, i18n } = useTranslation('marketing');
    // Hooks antes do early return: `post` e' null enquanto nenhum card foi aberto.
    const [tab, setTab] = useState('details');
    const [commentCount, setCommentCount] = useState(null);

    useEffect(() => {
        setTab('details');
        setCommentCount(null);
    }, [post?._id]);

    if (!post) return null;

    // So faz sentido comentar em post que existe na Meta — rascunho/agendado nao tem.
    const hasComments = commentTargets(post).length > 0;

    const previewAccounts = buildPreviewAccounts(post, accounts);
    const primaryPlatform = (post.platforms || [])[0];
    const account = previewAccounts[primaryPlatform];
    const username = account?.account_info?.username || account?.account_name || t('posts.preview.defaultAccount');

    const status = STATUS_MAP[post.status] || STATUS_MAP.draft;
    const isGraphImport = post.source === 'graph';
    const displayDate = post.published_date || post.scheduled_date;
    const canPublishNow = ['draft', 'scheduled', 'failed'].includes(post.status);
    const perf = post.performance || {};
    const hasPerf = ['likes', 'comments', 'shares', 'reach', 'impressions'].some((k) => perf[k]);
    const hashtags = post.content?.hashtags || [];
    const media = post.content?.media || [];

    const links = (post.platforms || [])
        .map((p) => ({ platform: p, url: post.external_ids?.[p]?.permalink }))
        .filter((l) => l.url);

    const perfMetrics = [
        { key: 'likes', icon: 'eva:heart-outline', label: t('posts.detail.likes') },
        { key: 'comments', icon: 'eva:message-circle-outline', label: t('posts.detail.comments') },
        { key: 'reach', icon: 'eva:radio-outline', label: t('posts.detail.reach') }
    ];

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-[980px] w-[95vw] rounded-2xl border-0 shadow-2xl bg-card p-0 overflow-hidden gap-0">
                <div className="grid md:grid-cols-[1.35fr_1fr] max-h-[86vh]">
                    <div className="relative bg-black flex items-center justify-center min-h-[300px]">
                        <MediaStage media={media} postType={post.post_type} t={t} />

                        <div className="absolute top-0 inset-x-0 flex items-center gap-2.5 px-4 py-3 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
                            {account?.profile_picture ? (
                                <img src={account.profile_picture} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-white/70" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-violet-500 ring-2 ring-white/70" />
                            )}
                            <span className="text-sm font-semibold text-white drop-shadow">{username}</span>
                            <span className="flex items-center gap-1.5 ml-auto">
                                {(post.platforms || []).map((p) => {
                                    const info = resolvePlatform(p);
                                    if (!info) return null;
                                    return <Iconify key={p} icon={info.icon} width={18} className="text-white drop-shadow" title={info.label} />;
                                })}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col min-h-0">
                        <div className="flex items-center justify-between gap-3 pl-5 pr-14 py-3.5 border-b border-border">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className={statusBadgeClass(status.tone)}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                    {t(status.labelKey)}
                                </span>
                                {isGraphImport && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                        <Iconify icon="eva:download-outline" width={10} /> {t('posts.table.imported')}
                                    </span>
                                )}
                            </div>
                            <span className="text-[11px] font-medium text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
                                {t(`posts.types.${post.post_type}`)}
                            </span>
                        </div>

                        {hasComments && (
                            <div className="flex items-center gap-1 px-5 pt-3 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setTab('details')}
                                    className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${tab === 'details' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60'}`}
                                >
                                    {t('posts.comments.tabDetails')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTab('comments')}
                                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${tab === 'comments' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60'}`}
                                >
                                    <Iconify icon="eva:message-circle-outline" width={14} />
                                    {t('posts.comments.tabComments')}
                                    {/* Antes de abrir a aba, o numero vem do sync da Graph;
                                        depois, da contagem real da thread carregada. */}
                                    {(commentCount ?? perf.comments) > 0 && (
                                        <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300">
                                            {commentCount ?? perf.comments}
                                        </span>
                                    )}
                                </button>
                            </div>
                        )}

                        {hasComments && tab === 'comments' && (
                            <MktPostComments post={post} onCountChange={setCommentCount} />
                        )}

                        <div className={`flex-1 overflow-y-auto px-5 py-4 space-y-4${hasComments && tab === 'comments' ? ' hidden' : ''}`}>
                            <div>
                                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{t('posts.preview.caption')}</p>
                                {post.content?.text ? (
                                    <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed">{post.content.text}</p>
                                ) : (
                                    <p className="text-sm text-muted-foreground italic">{t('posts.preview.noCaption')}</p>
                                )}
                                {hashtags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                                        {hashtags.map((h) => (
                                            <span key={h} className="text-[11px] font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">{h}</span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between text-sm border-t border-border pt-3">
                                <span className="text-xs text-muted-foreground">
                                    {post.status === 'published' ? t('posts.preview.publishedAt') : t('posts.preview.scheduledFor')}
                                </span>
                                <span className="text-foreground font-medium tabular-nums">{formatDate(displayDate, i18n.language)}</span>
                            </div>

                            {hasPerf && (
                                <div className="grid grid-cols-3 gap-2">
                                    {perfMetrics.map((m) => (
                                        <div key={m.key} className="rounded-xl border border-border bg-muted/30 p-2.5 text-center">
                                            <Iconify icon={m.icon} width={16} className="text-muted-foreground mx-auto mb-1" />
                                            <p className="text-sm font-bold text-foreground tabular-nums">{formatNumber(perf[m.key])}</p>
                                            <p className="text-[10px] text-muted-foreground">{m.label}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {post.status === 'failed' && post.error_message && (
                                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-3 text-xs text-red-700 dark:text-red-300">
                                    {post.error_message}
                                </div>
                            )}

                            {links.length > 0 && (
                                <div className="flex flex-wrap gap-3">
                                    {links.map((l) => {
                                        const info = resolvePlatform(l.platform);
                                        return (
                                            <a
                                                key={l.platform}
                                                href={l.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300 hover:underline"
                                            >
                                                {info && <Iconify icon={info.icon} width={14} style={{ color: info.color }} />}
                                                {t('posts.preview.openOn', { platform: info?.label || l.platform })}
                                                <Iconify icon="eva:external-link-outline" width={13} />
                                            </a>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-border bg-muted/30">
                            <button
                                type="button"
                                onClick={() => onDelete(post)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
                            >
                                <Iconify icon="eva:trash-2-outline" width={15} />
                                {t('posts.actions.delete')}
                            </button>
                            <div className="flex items-center gap-2">
                                {canPublishNow && (
                                    <button
                                        type="button"
                                        onClick={() => onPublish(post)}
                                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-border bg-card hover:bg-accent text-foreground text-xs font-medium transition-all"
                                    >
                                        <Iconify icon="eva:paper-plane-outline" width={15} />
                                        {t('posts.actions.publishNow')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onEdit(post)}
                                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium transition-all"
                                >
                                    <Iconify icon={isGraphImport ? 'eva:eye-outline' : 'eva:edit-2-outline'} width={15} />
                                    {isGraphImport ? t('posts.menu.viewDetails') : t('posts.menu.edit')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function statusBadgeClass(tone) {
    const base = 'text-xs font-medium flex items-center gap-1.5 px-2 py-0.5 rounded-full border';
    const map = {
        gray: 'bg-gray-50 text-gray-600 border-gray-200',
        blue: 'bg-blue-50 text-blue-700 border-blue-200',
        amber: 'bg-amber-50 text-amber-700 border-amber-200',
        green: 'bg-green-50 text-green-700 border-green-200',
        red: 'bg-red-50 text-red-700 border-red-200'
    };
    return `${base} ${map[tone] || map.gray}`;
}
